import type { FileResponse } from "@eu.mdotm.api/sphere";
import { base64 } from "rfc4648";
import contentDisposition from "content-disposition";
import type { AxiosResponse } from "axios";

export function downloadFileResponse(
	fileResponse: FileResponse,
	opts?: {
		fileName?: string;
	},
): void {
	const buf = new Uint8Array(fileResponse.data ?? base64.parse(fileResponse.realData as unknown as string));
	const blob = new Blob([buf], { type: "application/octet-stream" });
	downloadBlob(blob, {
		...opts,
		fileName: opts?.fileName ?? fileResponse.filename ?? "Untitled",
	});
}

export function downloadBlob(
	blob: Blob,
	opts: {
		fileName: string;
	},
): void {
	const url = window.URL.createObjectURL(blob);
	downloadUrl(url, opts);
	setTimeout(() => URL.revokeObjectURL(url));
}

/**
 * Hand a URL to the browser as a navigation, so a response carrying `Content-Disposition: attachment`
 * saves as a file without being read by script — which is what lets a cross-origin link download
 * where an XHR to it would be refused by CORS. `fileName` only applies to a same-origin or `blob:`
 * URL: for a cross-origin one the browser ignores `download` and the response's own
 * `Content-Disposition` names the file.
 */
export function downloadUrl(
	url: string,
	opts?: {
		fileName?: string;
	},
): void {
	const anchor = window.document.createElement("a");
	anchor.tabIndex = -1;
	anchor.style.opacity = "0";
	anchor.style.zIndex = "-1";
	anchor.style.position = "absolute";
	anchor.style.height = "0px";
	anchor.style.width = "0px";
	anchor.style.top = "0px";
	anchor.style.left = "0px";
	anchor.style.overflow = "hidden";
	anchor.href = url;
	anchor.download = opts?.fileName ?? "";
	document.body.appendChild(anchor);
	anchor.click();
	setTimeout(() => {
		document.body.removeChild(anchor);
	});
}

export async function downloadContentDisposition(response: AxiosResponse<File, any>): Promise<void> {
	const buffer = await response.data.arrayBuffer();
	const blob = new Blob([buffer], { type: "application/octet-stream" });
	const disposition = contentDisposition.parse(response.headers["content-disposition"]);
	const fileName = disposition.parameters.filename;
	downloadBlob(blob, { fileName });
}
